import {lazy, useEffect, Suspense} from "react"
import {useDispatch} from "react-redux"
import {createBrowserRouter, Navigate, Outlet, RouterProvider} from "react-router-dom"
import ErrorBoundary from "./ErrorBoundary/index"
import Landing from "./layout/Landing"
import SurveyLanding from "./layout/SurveyLanding"
import {setCredentials} from "./pages/Login/authSlice"
import RequiredAuth from "./routes/RequiredAuth"
import Loader from "./components/Loader"
import Box from "@mui/material/Box"
import MainHeaderComponent from "./components/MainHeaderComponent"
import Toaster from "./components/Toaster"
import LocationGate from "./components/LocationGate"
import Home from "./pages/Home"
import SiteVisitAuth from "./pages/SiteVisit/SiteVisitAuth"

// Location access is requested inside the router (LocationGate) for the
// commissioning lane; the site-visit lane asks at check-in/out time instead.
const RootShell = () => (
    <>
        <LocationGate />
        <Outlet />
    </>
)

// The statiq header belongs to the commissioning lane only — the landing page
// and the site-visit lane bring their own chrome.
const CommissioningShell = () => (
    <>
        <MainHeaderComponent />
        <Outlet />
    </>
)

const SiteSurveyShell = () => (
    <>
        <MainHeaderComponent />
        <Outlet />
    </>
)

function App() {
    const CommissioningForm = lazy(() => import("./pages/CommissioningForm"))
    const TicketingDetails = lazy(() => import("./pages/CommissioningForm/Features/TicketingDetails"))
    const HardwareDetails = lazy(() => import("./pages/CommissioningForm/Features/HardwareDetails"))
    const SiteImagesDetailsOne = lazy(
        () => import("./pages/CommissioningForm/Features/SiteImagesDetails/SiteImagesDetailsOne")
    )
    const SiteImagesDetailsTwo = lazy(
        () => import("./pages/CommissioningForm/Features/SiteImagesDetails/SiteImagesDetailsTwo")
    )
    const FinalRemarks = lazy(() => import("./pages/CommissioningForm/Features/FinalRemarks"))

    const SvLogin = lazy(() => import("./pages/SiteVisit/Features/Login"))
    const SvTickets = lazy(() => import("./pages/SiteVisit/Features/Tickets"))
    const SvTicketDetail = lazy(() => import("./pages/SiteVisit/Features/TicketDetail"))
    const SvMyVisits = lazy(() => import("./pages/SiteVisit/Features/MyVisits"))
    const SvProfile = lazy(() => import("./pages/SiteVisit/Features/Profile"))

    const SiteSurveyForm = lazy(() => import("./pages/SiteSurveyForm"))
    const SurveyDetails = lazy(() => import("./pages/SiteSurveyForm/Features/SurveyDetails"))
    const SiteAndLoad = lazy(() => import("./pages/SiteSurveyForm/Features/SiteAndLoad"))
    const ElectricalReadings = lazy(() => import("./pages/SiteSurveyForm/Features/ElectricalReadings"))
    const RemarksAndBoq = lazy(() => import("./pages/SiteSurveyForm/Features/RemarksAndBoq"))

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(setCredentials({token: ""}))
    }, [])

    const router = createBrowserRouter([
        {
            element: <RootShell />,
            children: [
                {
                    path: "/",
                    element: <Navigate to="/home" replace />,
                },
                {
                    path: "/home",
                    element: <Home />,
                    errorElement: <ErrorBoundary />,
                },
                {
                    path: "/site-visit",
                    errorElement: <ErrorBoundary />,
                    children: [
                        {
                            index: true,
                            element: <Navigate to="tickets" replace />,
                        },
                        {
                            path: "auth",
                            element: <SvLogin />,
                        },
                        {
                            element: <SiteVisitAuth />,
                            children: [
                                {
                                    path: "tickets",
                                    element: <SvTickets />,
                                },
                                {
                                    path: "tickets/:module/:id",
                                    element: <SvTicketDetail />,
                                },
                                {
                                    path: "my-visits",
                                    element: <SvMyVisits />,
                                },
                                {
                                    path: "profile",
                                    element: <SvProfile />,
                                },
                            ],
                        },
                    ],
                },
                {
                    element: <CommissioningShell />,
                    errorElement: <ErrorBoundary />,
                    children: [
                        {
                            path: "/commissioning-form/auth",
                            element: <Landing />,
                            errorElement: <ErrorBoundary />,
                        },
                        {
                            element: <RequiredAuth />,
                            errorElement: <ErrorBoundary />,
                            children: [
                                {
                                    path: "commissioning-form",
                                    errorElement: <ErrorBoundary />,
                                    element: <CommissioningForm />,
                                    children: [
                                        {
                                            index: true, // Add this
                                            element: <Navigate to="ticketing-details" replace />,
                                        },
                                        {
                                            path: "ticketing-details",
                                            element: <TicketingDetails />,
                                        },
                                        {
                                            path: "hardware-details",
                                            element: <HardwareDetails />,
                                        },
                                        {
                                            path: "site-images-1",
                                            element: <SiteImagesDetailsOne />,
                                        },
                                        {
                                            path: "site-images-2",
                                            element: <SiteImagesDetailsTwo />,
                                        },
                                        {
                                            path: "final-remarks",
                                            element: <FinalRemarks />,
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    element: <SiteSurveyShell/>,
                    errorElement: <ErrorBoundary/>,
                    children: [
                        {
                            path: "/site-survey/auth",
                            element: <SurveyLanding/>,
                            errorElement: <ErrorBoundary/>
                        },
                        {
                            element: <RequiredAuth authPath="/site-survey/auth"/>,
                            errorElement: <ErrorBoundary/>,
                            children: [
                        {
                            path: "site-survey",
                            element: <SiteSurveyForm/>,
                            errorElement: <ErrorBoundary/>,
                            children: [
                                {
                                    index: true,
                                    element: <Navigate to="survey-details" replace />,
                                },
                                {
                                    path: "survey-details",
                                    element: <SurveyDetails/>,
                                },
                                {
                                    path: "site-and-load",
                                    element: <SiteAndLoad/>,
                                },
                                {
                                    path: "electrical-readings",
                                    element: <ElectricalReadings/>,
                                },
                                {
                                    path: "remarks-and-boq",
                                    element: <RemarksAndBoq/>,
                                },
                            ],
                        },
                            ],
                        },
                    ]
                },
            ],
        },
    ])

    return (
        <Suspense
            fallback={
                <Box
                    sx={{
                        width: "100%",
                        height: "100vh",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}>
                    <Loader />
                </Box>
            }>
            <Box className="main_wrapper">
                <Toaster vertical={"top"} horizontal={"center"} autoHideDuration={3000} />
                <RouterProvider router={router} />
            </Box>
        </Suspense>
    )
}

export default App
